import exifr from 'exifr';
import { FileMetadata } from '../types';

export class ExifExtractor {
  private static readonly EXTRACTION_TIMEOUT = 5000; // 5 seconds timeout
  private static readonly CHUNK_SIZE_MOBILE = 512 * 1024; // 512KB for mobile
  private static readonly CHUNK_SIZE_DESKTOP = 256 * 1024; // 256KB for desktop

  static async extractMetadata(
    file: File,
    isIOSSafari: boolean = false
  ): Promise<FileMetadata> {
    console.log(`🔍 Starting EXIF extraction for ${file.name} (iOS Safari: ${isIOSSafari})`);
    
    const startTime = performance.now();
    
    try {
      // Create blob URL for the file
      const blobUrl = URL.createObjectURL(file);
      
      try {
        const metadata = await this.extractFromBlobUrl(blobUrl, isIOSSafari);
        const processingTime = performance.now() - startTime;
        
        console.log(`✅ EXIF extraction complete for ${file.name} (${Math.round(processingTime)}ms):`, metadata);
        
        return metadata;
        
      } finally {
        // Always clean up the blob URL
        URL.revokeObjectURL(blobUrl);
      }
      
    } catch (error) {
      const processingTime = performance.now() - startTime;
      console.warn(`⚠️ EXIF extraction failed for ${file.name} (${Math.round(processingTime)}ms):`, error);
      
      return {
        hasCreationDate: false,
        hasLocation: false,
      };
    }
  }

  private static async extractFromBlobUrl(
    blobUrl: string,
    isIOSSafari: boolean
  ): Promise<FileMetadata> {
    // Different strategies for different devices
    const chunkSize = isIOSSafari ? this.CHUNK_SIZE_MOBILE : this.CHUNK_SIZE_DESKTOP;
    
    if (isIOSSafari) {
      console.log('📱 Using iOS Safari optimized EXIF extraction');
      return await this.extractIOSSafariOptimized(blobUrl);
    } else {
      console.log('💻 Using standard EXIF extraction');
      return await this.extractStandard(blobUrl, chunkSize);
    }
  }

  private static async extractIOSSafariOptimized(blobUrl: string): Promise<FileMetadata> {
    try {
      // For iOS Safari, try full blob first as Apple might preserve EXIF differently
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      
      console.log(`📱 iOS Safari: Processing blob of size ${blob.size} bytes`);
      
      // Minimal delay for iOS Safari stability
      await new Promise(resolve => setTimeout(resolve, 10)); // Reduced from 50ms
      
      let exifData;
      
      try {
        // Try with full blob first
        exifData = await Promise.race([
          exifr.parse(blob, {
            gps: true,
            exif: true,
            tiff: true,
            xmp: false,
            icc: false,
            iptc: false,
            jfif: false,
            ihdr: false,
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('iOS Safari EXIF timeout')), 3000)
          )
        ]);
        
      } catch (fullBlobError) {
        console.warn('📱 iOS Safari: Full blob EXIF failed, trying chunk approach:', fullBlobError);
        
        // Fallback to chunk approach
        const chunk = blob.slice(0, this.CHUNK_SIZE_MOBILE);
        exifData = await exifr.parse(chunk, {
          gps: true,
          exif: true,
          tiff: true,
          xmp: false,
          icc: false,
          iptc: false,
          jfif: false,
          ihdr: false,
        });
      }
      
      return this.parseExifData(exifData);
      
    } catch (error) {
      console.warn('📱 iOS Safari EXIF extraction failed:', error);
      throw error;
    }
  }

  private static async extractStandard(blobUrl: string, chunkSize: number): Promise<FileMetadata> {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      
      // Use chunk approach for better memory management
      const chunk = blob.slice(0, chunkSize);
      
      const exifData = await Promise.race([
        exifr.parse(chunk, {
          gps: true,
          exif: true,
          tiff: true,
          xmp: false,
          icc: false,
          iptc: false,
          jfif: false,
          ihdr: false,
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('EXIF extraction timeout')), this.EXTRACTION_TIMEOUT)
        )
      ]);
      
      return this.parseExifData(exifData);
      
    } catch (error) {
      console.warn('💻 Standard EXIF extraction failed:', error);
      throw error;
    }
  }

  private static parseExifData(exifData: any): FileMetadata {
    const metadata: FileMetadata = {
      hasCreationDate: false,
      hasLocation: false,
    };

    if (!exifData) {
      return metadata;
    }

    // Extract GPS location
    if (exifData.latitude && exifData.longitude) {
      metadata.latitude = exifData.latitude;
      metadata.longitude = exifData.longitude;
      metadata.hasLocation = true;
      console.log(`📍 GPS found: ${metadata.latitude}, ${metadata.longitude}`);
    } else if (exifData.GPSLatitude && exifData.GPSLongitude && 
               exifData.GPSLatitudeRef && exifData.GPSLongitudeRef) {
      // Convert DMS to decimal degrees
      const lat = this.convertDMSToDD(
        exifData.GPSLatitude[0],
        exifData.GPSLatitude[1],
        exifData.GPSLatitude[2],
        exifData.GPSLatitudeRef
      );
      const lng = this.convertDMSToDD(
        exifData.GPSLongitude[0],
        exifData.GPSLongitude[1],
        exifData.GPSLongitude[2],
        exifData.GPSLongitudeRef
      );
      
      metadata.latitude = lat;
      metadata.longitude = lng;
      metadata.hasLocation = true;
      console.log(`📍 GPS converted from DMS: ${lat}, ${lng}`);
    }

    // Extract date information
    if (exifData.DateTimeOriginal) {
      metadata.createdAt = this.formatExifDate(exifData.DateTimeOriginal);
      metadata.hasCreationDate = true;
      console.log(`📅 Date taken: ${metadata.createdAt}`);
    } else if (exifData.DateTime) {
      metadata.createdAt = this.formatExifDate(exifData.DateTime);
      metadata.hasCreationDate = true;
      console.log(`📅 Date time: ${metadata.createdAt}`);
    } else if (exifData.CreateDate) {
      metadata.createdAt = this.formatExifDate(exifData.CreateDate);
      metadata.hasCreationDate = true;
      console.log(`📅 Create date: ${metadata.createdAt}`);
    }

    // Extract image dimensions
    if (exifData.PixelXDimension && exifData.PixelYDimension) {
      metadata.width = exifData.PixelXDimension;
      metadata.height = exifData.PixelYDimension;
    } else if (exifData.ExifImageWidth && exifData.ExifImageHeight) {
      metadata.width = exifData.ExifImageWidth;
      metadata.height = exifData.ExifImageHeight;
    }

    // Store raw EXIF data for debugging
    metadata.exifData = exifData;

    return metadata;
  }

  private static convertDMSToDD(degrees: number, minutes: number, seconds: number, direction: string): number {
    let dd = degrees + minutes / 60 + seconds / 3600;
    
    if (direction === 'S' || direction === 'W') {
      dd = dd * -1;
    }
    
    return dd;
  }

  private static formatExifDate(date: any): string {
    try {
      if (typeof date === 'string') {
        // EXIF dates are typically in format "YYYY:MM:DD HH:MM:SS"
        if (date.includes(':') && date.split(':').length >= 3) {
          const parts = date.split(' ');
          if (parts.length === 2) {
            const datePart = parts[0].replace(/:/g, '-');
            const timePart = parts[1];
            return `${datePart}T${timePart}`;
          }
        }
        return date;
      } else if (date instanceof Date) {
        return date.toISOString();
      } else {
        return String(date);
      }
    } catch (error) {
      console.warn('⚠️ Error formatting EXIF date:', error);
      return String(date);
    }
  }

  static async extractBatch(
    files: File[],
    isIOSSafari: boolean = false,
    onProgress?: (progress: number, currentFile: string) => void
  ): Promise<FileMetadata[]> {
    console.log(`🔍 Starting batch EXIF extraction for ${files.length} files`);
    
    const results: FileMetadata[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (onProgress) {
        onProgress((i / files.length) * 100, file.name);
      }

      try {
        const metadata = await this.extractMetadata(file, isIOSSafari);
        results.push(metadata);

        // Minimal delay between extractions to prevent blocking UI on iOS Safari
        if (isIOSSafari && i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10)); // Reduced from 50ms
        }

      } catch (error) {
        console.error(`❌ Failed to extract EXIF from ${file.name}:`, error);
        
        // Add empty metadata for failed extractions
        results.push({
          hasCreationDate: false,
          hasLocation: false,
        });
      }
    }

    if (onProgress) {
      onProgress(100, 'Complete');
    }

    const withLocation = results.filter(r => r.hasLocation).length;
    const withDate = results.filter(r => r.hasCreationDate).length;

    console.log(`✅ Batch EXIF extraction complete:`);
    console.log(`  - Files processed: ${results.length}`);
    console.log(`  - With location: ${withLocation}`);
    console.log(`  - With date: ${withDate}`);

    return results;
  }

  static isImageFile(file: File): boolean {
    const imageTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/tiff',
      'image/heic',
      'image/heif',
      'image/webp'
    ];
    
    return imageTypes.includes(file.type.toLowerCase()) || 
           /\.(jpe?g|png|tiff?|heic|heif|webp)$/i.test(file.name);
  }
}
